
import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, Plus, ChevronRight, BrainCircuit, TrendingUp, FileText, Loader2, ClipboardList, Sparkles, Mic, Target, History, Edit3, Activity, Cpu, Trash2, Save
} from 'lucide-react';
import { Patient, ClinicalCycle, Therapist, AudioAnalysis, ClinicalRecord } from '../types';
import { INDICATORS } from '../constants';
import CycleForm from './CycleForm';
import ReportModal from './ReportModal';
import PatientEditModal from './PatientEditModal';
import Prontuario from './Prontuario';
import PatientFiles from './PatientFiles';
import PatientFrequency from './PatientFrequency';
import { createClinicalRecord } from '../services/patientsApi';
import { describeError } from '../utils/describeError';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar 
} from 'recharts';

interface PatientDetailsProps {
  patient: Patient;
  therapist: Therapist;
  onBack: () => void;
  onSaveCycle: (cycle: ClinicalCycle) => Promise<void> | void;
  onUpdatePatient: (patient: Patient) => void | Promise<void>;
  onUpdateReport?: (cycle: ClinicalCycle) => void;
  onDeleteCycle: (cycleId: string) => void;
  isGeneratingIA?: boolean;
  patients: Patient[];
  recordingsList: any[];
  setRecordingsList: React.Dispatch<React.SetStateAction<any[]>>;
  onSaveAIAnalysis: (patientId: string, recordingId: string, analysis: AudioAnalysis, attachToProntuario?: boolean) => void;
  onDeleteTranscription: (recordingId: string) => void;
  initialOpenEditProfile?: boolean;
  onClearInitialOpenEditProfile?: () => void;
  initialSubTab?: 'evolution' | 'prontuario' | 'files';
  onClearInitialSubTab?: () => void;
  initialProntuarioDate?: string;
  onClearInitialProntuarioDate?: () => void;
  onRecordsChange?: (patientId: string, records: ClinicalRecord[]) => void;
}

const PatientDetails: React.FC<PatientDetailsProps> = ({ 
  patient, 
  onBack, 
  onSaveCycle, 
  onUpdatePatient,
  onUpdateReport,
  onDeleteCycle, 
  therapist, 
  isGeneratingIA,
  patients,
  recordingsList,
  setRecordingsList,
  onSaveAIAnalysis,
  onDeleteTranscription,
  initialOpenEditProfile,
  onClearInitialOpenEditProfile,
  initialSubTab,
  onClearInitialSubTab,
  initialProntuarioDate,
  onClearInitialProntuarioDate,
  onRecordsChange
}) => {
  const activeProfile = useMemo(() => {
    try {
      const profilesSaved = localStorage.getItem('allodos-user-profiles');
      const activeId = localStorage.getItem('allodos-active-profile-id') || '1';
      if (profilesSaved) {
        const list = JSON.parse(profilesSaved);
        return list.find((p: any) => p.id === activeId) || list[0];
      }
    } catch (e) {}
    return { id: '1', name: 'Dra. Marina Lopes', role: 'Administrador / Psicóloga', email: 'contato@clinicaevoluir.com', status: 'Ativo' };
  }, []);

  const isSecretary = useMemo(() => {
    const role = (activeProfile?.role || '').toLowerCase();
    return role.includes('recepcionista') || role.includes('recep') || role.includes('secretária') || role.includes('secretaria');
  }, [activeProfile]);

  // Ao abrir um paciente, a primeira aba é sempre o Cadastro — vale para todos
  // os perfis, não só para a recepção.
  const [activeSubTab, setActiveSubTab] = useState<'cadastro' | 'evolution' | 'prontuario' | 'files' | 'frequencia'>('cadastro');
  const [showCycleForm, setShowCycleForm] = useState(false);
  const [editingCycle, setEditingCycle] = useState<ClinicalCycle | undefined>(undefined);
  const [showReport, setShowReport] = useState<ClinicalCycle | null>(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(patient.cycles?.length ? patient.cycles[patient.cycles.length - 1].id : null);
  const [selectedAudioAnalysis, setSelectedAudioAnalysis] = useState<AudioAnalysis | null>(null);
  
  const [editedDetailTranscription, setEditedDetailTranscription] = useState('');
  const [editedDetailSynthesis, setEditedDetailSynthesis] = useState('');
  const [editedDetailStrategicPlan, setEditedDetailStrategicPlan] = useState('');
  const [isEditingAnalysis, setIsEditingAnalysis] = useState(false);
  const [isSavingAnalysis, setIsSavingAnalysis] = useState(false);
  const [cadastroFeedback, setCadastroFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  React.useEffect(() => {
    if (initialSubTab) {
      if (!isSecretary) {
        setActiveSubTab(initialSubTab);
      }
      if (onClearInitialSubTab) {
        onClearInitialSubTab();
      }
    }
  }, [initialSubTab, onClearInitialSubTab, isSecretary]);

  React.useEffect(() => {
    if (initialOpenEditProfile) {
      setShowEditProfile(true);
      if (onClearInitialOpenEditProfile) {
        onClearInitialOpenEditProfile();
      }
    }
  }, [initialOpenEditProfile, onClearInitialOpenEditProfile]);

  React.useEffect(() => {
    const cycles = patient.cycles ?? [];
    if (cycles.length === 0) {
      if (selectedCycleId !== null) {
        setSelectedCycleId(null);
      }
      return;
    }
    if (!selectedCycleId || !cycles.some((cycle) => cycle.id === selectedCycleId)) {
      setSelectedCycleId(cycles[cycles.length - 1].id);
    }
  }, [patient.cycles, selectedCycleId]);

  React.useEffect(() => {
    if (selectedAudioAnalysis) {
      setEditedDetailTranscription(selectedAudioAnalysis.transcription || '');
      setEditedDetailSynthesis(selectedAudioAnalysis.sessionSynthesis || '');
      setEditedDetailStrategicPlan(selectedAudioAnalysis.strategicPlan || '');
      setIsEditingAnalysis(false);
    }
  }, [selectedAudioAnalysis]);

  const handleSaveEditedAnalysis = () => {
    if (!selectedAudioAnalysis) return;
    setIsSavingAnalysis(true);
    
    const updatedAnalysis: AudioAnalysis = {
      ...selectedAudioAnalysis,
      transcription: editedDetailTranscription,
      sessionSynthesis: editedDetailSynthesis,
      strategicPlan: editedDetailStrategicPlan
    };

    const updatedAudioAnalyses = patient.audioAnalyses?.map(a => 
      a.id === selectedAudioAnalysis.id ? updatedAnalysis : a
    ) || [];

    const updatedPatient: Patient = {
      ...patient,
      audioAnalyses: updatedAudioAnalyses
    };

    onUpdatePatient(updatedPatient);
    setSelectedAudioAnalysis(updatedAnalysis);
    setIsEditingAnalysis(false);
    setIsSavingAnalysis(false);
  };

  const handleAttachAnalysisToProntuario = async () => {
    if (!selectedAudioAnalysis) return;

    const currentTranscription = editedDetailTranscription || selectedAudioAnalysis.transcription;
    const currentSynthesis = editedDetailSynthesis || selectedAudioAnalysis.sessionSynthesis;
    const currentStrategicPlan = editedDetailStrategicPlan || selectedAudioAnalysis.strategicPlan;

    const formattedContent = `
      <p><strong>Transcrição Literal da Sessão (Supervisor IA - ${selectedAudioAnalysis.approach}):</strong></p>
      <p>${currentTranscription.replace(/\n/g, '<br/>')}</p>
      <br/>
      <p><strong>Síntese e Fenômenos Clínicos:</strong></p>
      <p>${currentSynthesis.replace(/\n/g, '<br/>')}</p>
      <br/>
      <p><strong>Plano Estratégico de Manejo:</strong></p>
      <p>${currentStrategicPlan.replace(/\n/g, '<br/>')}</p>
    `.trim();

    // O registro precisa ir para a API: só no estado ele sumia na próxima
    // recarga do paciente.
    let newRecord: ClinicalRecord;
    try {
      newRecord = await createClinicalRecord(patient.id, {
        date: new Date().toISOString().split('T')[0],
        content: formattedContent,
        approach: selectedAudioAnalysis.approach,
        aiAnalysis: `${currentSynthesis}\n\n${currentStrategicPlan}`,
      });
    } catch (err) {
      console.error(err);
      alert(`Não foi possível anexar ao prontuário: ${describeError(err)}`);
      return;
    }

    const updatedRecords = patient.records ? [...patient.records, newRecord] : [newRecord];
    onRecordsChange?.(patient.id, updatedRecords);
    
    const updatedPatient: Patient = {
      ...patient,
      records: updatedRecords
    };

    if (isEditingAnalysis) {
      const updatedAnalysis: AudioAnalysis = {
        ...selectedAudioAnalysis,
        transcription: currentTranscription,
        sessionSynthesis: currentSynthesis,
        strategicPlan: currentStrategicPlan
      };
      updatedPatient.audioAnalyses = patient.audioAnalyses?.map(a => 
        a.id === selectedAudioAnalysis.id ? updatedAnalysis : a
      ) || [];
      setSelectedAudioAnalysis(updatedAnalysis);
      setIsEditingAnalysis(false);
    }

    onUpdatePatient(updatedPatient);
    alert("Análise anexada com sucesso ao prontuário do paciente!");
  };
  
  const selectedCycle = patient.cycles?.find(c => c.id === selectedCycleId);
  
  const formatDateForChart = (dateStr: string) => {
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
    const parts = dateStr.split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr;
  };

  const getInitials = (name: string) => {
    const parts = (name || '').trim().split(' ');
    if (parts.length === 0 || !parts[0]) return '??';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  };

  const radarData = useMemo(() => {
    if (!selectedCycle) return [];
    return (selectedCycle.scores ?? []).map(s => {
      const ind = INDICATORS.find(i => i.id === s.indicatorId);
      return {
        subject: ind?.nome || 'Indicador',
        value: s.score,
        fullMark: 10,
      };
    });
  }, [selectedCycle]);

  const lineData = useMemo(() => {
    // Ordena os ciclos cronologicamente (do mais antigo para o mais novo)
    const sortedCycles = [...(patient.cycles ?? [])].sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    return sortedCycles.map(c => {
      const posScores = (c.scores ?? []).filter(s => {
        const ind = INDICATORS.find(i => i.id === s.indicatorId);
        return ind?.eixo === 'positive';
      });
      const negScores = (c.scores ?? []).filter(s => {
        const ind = INDICATORS.find(i => i.id === s.indicatorId);
        return ind?.eixo === 'negative';
      });

      const mediaPos = posScores.length > 0 
        ? Number((posScores.reduce((acc, curr) => acc + curr.score, 0) / posScores.length).toFixed(1))
        : 0;
      
      const mediaNeg = negScores.length > 0 
        ? Number((negScores.reduce((acc, curr) => acc + curr.score, 0) / negScores.length).toFixed(1))
        : 0;

      return {
        name: formatDateForChart(c.date),
        sofrimento: c.subjectiveDistress,
        positivos: mediaPos,
        negativos: mediaNeg,
      };
    });
  }, [patient.cycles]);

  return (
    <div className="space-y-8 animate-in fade-in duration-700 max-w-[1600px] mx-auto pb-12 px-4">
      {/* Barra de Navegação Superior */}
      <div className="flex flex-col md:flex-row items-center justify-center bg-white dark:bg-slate-900 p-5 rounded-[2rem] shadow-sm border border-slate-200 dark:border-slate-800 gap-6 w-full">
        <div className="flex flex-wrap items-center justify-center gap-3 w-full">
          <button 
            onClick={() => setActiveSubTab('cadastro')}
            className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-all shadow-sm border ${
              activeSubTab === 'cadastro' 
                ? 'bg-indigo-600 text-white border-indigo-600 scale-105 shadow-md shadow-indigo-100 dark:shadow-none' 
                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-800'
            }`}
          >
            Cadastro do Paciente
          </button>
          {!isSecretary && (
            <>
              <button 
                onClick={() => setActiveSubTab('prontuario')}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-all shadow-sm border ${
                  activeSubTab === 'prontuario' 
                    ? 'bg-indigo-600 text-white border-indigo-600 scale-105 shadow-md shadow-indigo-100 dark:shadow-none' 
                    : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-800'
                }`}
              >
                Prontuário ({patient.records?.length || 0})
              </button>
              <button 
                onClick={() => setActiveSubTab('evolution')}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-all shadow-sm border ${
                  activeSubTab === 'evolution' 
                    ? 'bg-indigo-600 text-white border-indigo-600 scale-105 shadow-md shadow-indigo-100 dark:shadow-none' 
                    : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-800'
                }`}
              >
                Gestão de Ciclos ({patient.cycles?.length || 0})
              </button>
              <button 
                onClick={() => setActiveSubTab('files')}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-all shadow-sm border ${
                  activeSubTab === 'files' 
                    ? 'bg-indigo-600 text-white border-indigo-600 scale-105 shadow-md shadow-indigo-100 dark:shadow-none' 
                    : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-800'
                }`}
              >
                Arquivos ({patient.files?.length || 0})
              </button>
              <button 
                onClick={() => setActiveSubTab('frequencia')}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-2xl text-[10px] sm:text-[11px] font-black uppercase tracking-wider transition-all shadow-sm border ${
                  activeSubTab === 'frequencia' 
                    ? 'bg-indigo-600 text-white border-indigo-600 scale-105 shadow-md shadow-indigo-100 dark:shadow-none' 
                    : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-800'
                }`}
              >
                Frequência
              </button>
            </>
          )}
        </div>
      </div>

      {activeSubTab === 'cadastro' && (
        <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm animate-in fade-in duration-300">
          <div className="mb-6 flex items-center justify-between border-b dark:border-slate-800 pb-4">
            <div>
              <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider">Ficha Cadastral do Paciente</h3>
              <p className="text-xs text-slate-400 dark:text-slate-500 font-bold mt-1">Gerencie todos os dados pessoais, contatos e profissionais vinculados</p>
            </div>
          </div>
          {/* Bloco 28: a confirmação fica abaixo do botão "Salvar Alterações"
              (prop feedback), não mais no topo da página. */}
          <PatientEditModal 
            feedback={cadastroFeedback}
            patient={patient} 
            onClose={() => {}} 
            onSave={async (updatedPatient) => {
              // Antes o clique não dava retorno nenhum (nem sucesso, nem erro).
              setCadastroFeedback(null);
              try {
                await onUpdatePatient(updatedPatient);
                setCadastroFeedback({ ok: true, msg: 'Alterações salvas com sucesso.' });
              } catch (err) {
                setCadastroFeedback({ ok: false, msg: `Não foi possível salvar: ${describeError(err)}` });
              }
              window.setTimeout(() => setCadastroFeedback(null), 8000);
            }} 
            inline={true}
          />
        </div>
      )}

      {activeSubTab === 'evolution' && (
        <>
          {/* Dashboard Superior: Resumo e Lista de Histórico */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Quadro do Paciente (Summary) */}
            <div className="lg:w-[30%] bg-white dark:bg-slate-900 p-5 sm:p-10 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="w-28 h-28 bg-indigo-50 dark:bg-slate-800 rounded-[2.5rem] flex items-center justify-center text-indigo-600 dark:text-indigo-400 text-3xl sm:text-4xl font-black border border-indigo-100 dark:border-indigo-900/50 shadow-inner">{getInitials(patient.name)}</div>
                <button onClick={() => setShowEditProfile(true)} className="absolute -bottom-2 -right-2 p-3 bg-indigo-600 text-white rounded-2xl shadow-lg hover:scale-110 active:scale-95 transition-all border-4 border-white dark:border-slate-900"><Edit3 size={18} /></button>
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-slate-100 tracking-tight">{patient.name}</h3>
              <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-black mb-6 px-5 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-full border border-indigo-100 dark:border-indigo-800 uppercase tracking-widest mt-3">{patient.activeApproach.split('(')[0]}</p>
              <div className="mt-4 w-full pt-8 border-t dark:border-slate-800 text-left space-y-2">
                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Queixa Principal</p>
                <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-4 italic leading-relaxed font-medium">"{patient.mainComplaint || 'Sem descrição clínica inicial'}"</p>
              </div>

              <div className="mt-6 w-full pt-6 border-t dark:border-slate-800 text-left space-y-3">
                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Medicamentos em Uso</p>
                {Array.isArray(patient.medications) && patient.medications.length > 0 ? (
                  <div className="overflow-hidden border border-slate-100 dark:border-slate-800 rounded-2xl">
                    <table className="w-full text-left text-[10px]">
                      <thead className="bg-slate-50 dark:bg-slate-800/50">
                        <tr>
                          <th className="p-2 font-black text-slate-400 uppercase tracking-widest">Nome</th>
                          <th className="p-2 font-black text-slate-400 uppercase tracking-widest">Qtd</th>
                          <th className="p-2 font-black text-slate-400 uppercase tracking-widest">Hora</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-slate-800">
                        {patient.medications.map((med, idx) => (
                          <tr key={idx} className="dark:text-slate-300">
                            <td className="p-2 font-bold">{med.name}</td>
                            <td className="p-2">{med.quantity}</td>
                            <td className="p-2">{med.time}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-slate-600 dark:text-slate-400 italic font-medium ml-1">
                    {typeof patient.medications === 'string' && patient.medications.trim() !== '' 
                      ? patient.medications 
                      : 'Nenhum medicamento registrado'}
                  </p>
                )}
              </div>
            </div>

            {/* Quadro de Histórico (Case History) */}
            <div className="lg:w-[70%] bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col">
              <div className="px-5 sm:px-8 py-5 border-b dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex flex-wrap justify-between items-center gap-3">
                <h4 className="text-[10px] font-black text-slate-800 dark:text-slate-200 uppercase tracking-[0.25em] flex items-center gap-3"><ClipboardList size={18} className="text-indigo-500" /> Histórico de Ciclos Clínicos</h4>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{patient.cycles?.length ?? 0} REGISTROS</span>
                  <button 
                    onClick={() => { setEditingCycle(undefined); setShowCycleForm(true); }} 
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-black flex items-center gap-1 transition-all uppercase tracking-widest hover:scale-105 active:scale-95 shadow-md shadow-indigo-100 dark:shadow-none"
                  >
                    <Plus size={10} /> Novo Ciclo
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[400px] flex-1 custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {(patient.cycles ?? []).slice().reverse().map(cycle => (
                      <tr 
                        key={cycle.id} 
                        className={`text-xs hover:bg-indigo-50/30 dark:hover:bg-indigo-900/20 transition-colors cursor-pointer ${selectedCycleId === cycle.id ? 'bg-indigo-50/50 dark:bg-indigo-900/40' : ''}`} 
                        onClick={() => setSelectedCycleId(cycle.id)}
                      >
                        <td className="px-4 sm:px-8 py-4 sm:py-6 font-black text-slate-700 dark:text-slate-300">
                          <div className="flex items-center gap-3">
                            <Activity size={16} className="text-indigo-400" />
                            <span className="text-sm tracking-tight">{formatDateForChart(cycle.date)}</span>
                          </div>
                        </td>
                        <td className="hidden sm:table-cell px-4 sm:px-8 py-4 sm:py-6">
                           <div className="flex gap-2">
                             <span className="text-[9px] font-black bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-3 py-1.5 rounded-full uppercase tracking-widest">Sofrimento: {cycle.subjectiveDistress}</span>
                           </div>
                        </td>
                        <td className="px-4 sm:px-8 py-4 sm:py-6 text-right">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <button onClick={(e) => { e.stopPropagation(); setEditingCycle(cycle); setShowCycleForm(true); }} className="text-indigo-600 bg-indigo-50 hover:bg-indigo-100 font-black text-[10px] uppercase tracking-widest px-4 py-2.5 rounded-xl transition-all">Editar</button>
                            <button onClick={(e) => { e.stopPropagation(); setShowReport(cycle); }} className="text-white bg-indigo-600 hover:bg-indigo-700 font-black text-[10px] uppercase tracking-widest px-5 py-2.5 rounded-xl transition-all shadow-md active:scale-95">Relatório Completo</button>
                            <button 
                              onClick={(e) => { e.stopPropagation(); onDeleteCycle(cycle.id); }} 
                              className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                              title="Excluir Ciclo"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(patient.cycles?.length ?? 0) === 0 && (
                  <div className="p-24 text-center text-slate-400 italic text-sm">Nenhum ciclo clínico registrado para este paciente.</div>
                )}
              </div>
            </div>
          </div>

          {/* Análise IA Longitudinal */}
          <div className="bg-indigo-900 dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] text-white p-5 sm:p-10 shadow-xl relative overflow-hidden transition-all duration-700 border dark:border-slate-800">
            <div className="flex items-center justify-between mb-8 relative z-10">
              <h4 className="text-xl font-black uppercase tracking-[0.2em] flex items-center gap-4">
                <BrainCircuit size={32} className="text-indigo-300 animate-pulse"/> 
                Análise IA Longitudinal
              </h4>
              {isGeneratingIA && (
                <div className="flex items-center gap-3 bg-indigo-500/30 px-6 py-2.5 rounded-full border border-indigo-400/30">
                  <Loader2 className="animate-spin text-white" size={16} />
                  <span className="text-[10px] font-black uppercase tracking-widest text-indigo-100">Analisando Dados...</span>
                </div>
              )}
            </div>
            
            <div className="relative z-10 min-h-[140px]">
              {isGeneratingIA ? (
                <div className="space-y-8 animate-in fade-in slide-in-from-top-6 duration-500">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                       <span className="text-xs font-black text-indigo-300 uppercase tracking-widest flex items-center gap-3"><Cpu size={18} /> Mapeando Indicadores Clínicos...</span>
                       <span className="text-xs font-black text-indigo-400">PROCESSANDO</span>
                    </div>
                    <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden border border-white/10 relative">
                      <div className="absolute inset-0 bg-indigo-500/20"></div>
                      <div 
                        className="h-full bg-indigo-400 shadow-[0_0_20px_rgba(129,140,248,0.6)] transition-all duration-1000 ease-out animate-shimmer" 
                        style={{ width: '65%', backgroundSize: '200% 100%', backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)' }}
                      ></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="h-4 bg-white/5 rounded-full w-full animate-pulse"></div>
                    <div className="h-4 bg-white/5 rounded-full w-[90%] animate-pulse [animation-delay:0.15s]"></div>
                    <div className="h-4 bg-white/5 rounded-full w-[85%] animate-pulse [animation-delay:0.3s]"></div>
                    <div className="h-4 bg-white/5 rounded-full w-[95%] animate-pulse [animation-delay:0.45s]"></div>
                  </div>
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-lg leading-relaxed text-indigo-50 italic font-medium animate-in fade-in slide-in-from-bottom-4 duration-700">
                  {selectedCycle?.aiInsight || "Selecione um ciclo no histórico acima para visualizar a interpretação estratégica gerada pela IA."}
                </div>
              )}
            </div>
            
            <div className="absolute -right-20 -bottom-20 text-white/5 opacity-10 pointer-events-none transform rotate-12" aria-hidden="true">
              <BrainCircuit size={360} />
            </div>
          </div>

          {/* Seção de Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-slate-900 p-5 sm:p-10 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex justify-between items-center mb-10">
                 <h4 className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] flex items-center gap-3"><Target size={22} className="text-indigo-500" /> Perfil Balanceado (Ciclo Atual)</h4>
              </div>
              <div className="h-[350px] w-full">
                {radarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                      <PolarGrid stroke="#94a3b8" strokeOpacity={0.2} />
                      <PolarAngleAxis dataKey="subject" fontSize={10} fontWeight="900" stroke="#94a3b8" />
                      <PolarRadiusAxis domain={[0, 10]} axisLine={false} tick={false} />
                      <Radar name="Intensidade" dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.4} strokeWidth={3} />
                      <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '1.5rem', fontSize: '12px', color: '#fff', padding: '12px', fontWeight: 'bold' }} />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-xs font-black uppercase tracking-[0.2em] border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[2.5rem]">Aguardando Registro</div>
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 p-5 sm:p-10 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex justify-between items-center mb-10">
                 <h4 className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] flex items-center gap-3"><TrendingUp size={22} className="text-emerald-500" /> Curva Evolutiva Longitudinal</h4>
              </div>
              <div className="h-[350px] w-full">
                {lineData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={lineData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="name" fontSize={10} fontWeight="900" stroke="#94a3b8" axisLine={false} tickLine={false} dy={10} />
                      <YAxis domain={[0, 10]} fontSize={10} fontWeight="900" stroke="#94a3b8" axisLine={false} tickLine={false} dx={-5} />
                      <Tooltip contentStyle={{ borderRadius: '1.5rem', border: 'none', backgroundColor: '#1e293b', color: '#fff', fontWeight: 'bold' }} itemStyle={{ fontSize: '11px', textTransform: 'uppercase' }} />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: '9px', fontWeight: '900', textTransform: 'uppercase', paddingTop: '30px' }} />
                      <Line 
                        type="monotone" 
                        name="Indicadores Positivos (+)" 
                        dataKey="positivos" 
                        stroke="#10b981" 
                        strokeWidth={4} 
                        dot={{ r: 6, fill: '#10b981', strokeWidth: 3, stroke: '#fff' }} 
                        activeDot={{ r: 8 }} 
                      />
                      <Line 
                        type="monotone" 
                        name="Indicadores Negativos (-)" 
                        dataKey="negativos" 
                        stroke="#ef4444" 
                        strokeWidth={4} 
                        dot={{ r: 6, fill: '#ef4444', strokeWidth: 3, stroke: '#fff' }} 
                        activeDot={{ r: 8 }} 
                      />
                      <Line type="monotone" name="Sofrimento" dataKey="sofrimento" stroke="#f59e0b" strokeWidth={3} strokeDasharray="8 5" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-xs font-black uppercase tracking-[0.2em] border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[2.5rem]">Aguardando Histórico</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {activeSubTab === 'prontuario' && (
        <Prontuario 
          patient={patient} 
          onUpdatePatient={onUpdatePatient} 
          patients={patients}
          recordingsList={recordingsList}
          setRecordingsList={setRecordingsList}
          onSaveAIAnalysis={onSaveAIAnalysis}
          onDeleteTranscription={onDeleteTranscription}
          initialRecordDate={initialProntuarioDate}
          onClearInitialRecordDate={onClearInitialProntuarioDate}
          onRecordsChange={onRecordsChange}
        />
      )}

      {activeSubTab === 'files' && (
        <PatientFiles patient={patient} onUpdatePatient={onUpdatePatient} />
      )}

      {activeSubTab === 'frequencia' && !isSecretary && <PatientFrequency patientId={patient.id} />}

      {/* Modais */}
      {showCycleForm && <CycleForm patient={patient} therapist={therapist} onClose={() => setShowCycleForm(false)} onSubmit={async (cycle) => { await onSaveCycle(cycle); setShowCycleForm(false); }} initialData={editingCycle} />}
      {showReport && (
        <ReportModal
          patient={patient}
          cycle={showReport}
          therapist={therapist}
          onClose={() => setShowReport(null)}
          onUpdateCycle={(updated) => {
            if (onUpdateReport) onUpdateReport(updated);
            else onUpdatePatient({ ...patient, cycles: (patient.cycles ?? []).map((c) => (c.id === updated.id ? updated : c)) });
          }}
        />
      )}
      {showEditProfile && (
        <PatientEditModal 
          patient={patient} 
          onClose={() => setShowEditProfile(false)} 
          onSave={(updated) => {
            onUpdatePatient(updated);
            setShowEditProfile(false);
          }} 
        />
      )}
    </div>
  );
};

export default PatientDetails;
